import {GeometryType} from '../enums/geometry-type.enum';

export abstract class BaseGeometry {
  selected = false;
  name = '';
  images: string[] = [];

  /**
   * The products on this shape, in the order they were put on it: a car park
   * is asphalt, then a sealing coat, then line paint. Each is priced on the
   * shape; the first is the one the map shows (its colour or texture).
   *
   * A new array on every change, never changed in place, for the same reason
   * as images below.
   */
  productIds: string[] = [];

  /**
   * The first product, or null. What a shape had before it could carry more
   * than one, and still what is stored as `product_id` for anything that only
   * reads that (older apps, API clients). Setting it puts that one product on
   * the shape, alone.
   */
  get productId(): string | null {
    return this.productIds && this.productIds.length ? this.productIds[0] : null;
  }

  set productId(id: string | null) {
    this.productIds = id ? [id] : [];
  }

  /** Puts these products on the shape, in this order, once each. */
  setProducts(ids: string[] | null | undefined) {
    this.productIds = BaseGeometry.productList(ids);
  }

  /** A list of product ids as a shape keeps it: strings, each once, in order. */
  static productList(ids: any): string[] {
    const list: string[] = [];
    (Array.isArray(ids) ? ids : []).forEach(id => {
      if (typeof id === 'string' && id && list.indexOf(id) === -1) {
        list.push(id);
      }
    });
    return list;
  }

  protected constructor(
    public readonly id: string,
    public readonly type: GeometryType
  ) {
  }

  abstract load(data: any);

  abstract toFirebaseData(deep?: boolean): any;

  abstract update();

  // A new array each time, never changed in place: the old one may be held by
  // a saved copy of this shape (the save cache, an undo step), and changing
  // it there made the save see no change and never send the photo.
  addImage(imageUrl: string) {
    this.images = [...(this.images || []), imageUrl];
  }

  removeImage(imageUrl: string) {
    const index = (this.images || []).indexOf(imageUrl);
    if (index !== -1) {
      this.images = this.images.filter((_, i) => i !== index);
    }
  }
}

